/**
 * The wager. A challenge is staked with any whole number of $CHAIN between the
 * platform's bounds, which the SERVER owns (`MIN_ENTRY_FEE` / `MAX_ENTRY_FEE`,
 * served at /api/challenges/fees with the quick-pick chips). These defaults only
 * stand in for the few hundred milliseconds before that answer, and when it never
 * comes; the server re-checks every amount whatever the client thinks.
 * Blockari's `fee.ts`, the same rules.
 */
export interface FeeBounds {
  readonly fees: readonly number[];
  readonly min: number;
  readonly max: number;
}

export const DEFAULT_BOUNDS: FeeBounds = { fees: [10, 25, 50, 100], min: 10, max: 10_000 };

/** The wager a first visit starts on, nudged into the bounds if they exclude it. */
export const FIRST_FEE = 10;

export function fallbackFee(b: FeeBounds): number {
  return Math.min(Math.max(FIRST_FEE, b.min), b.max);
}

/** An amount with thousands separators: "10,000". */
export function fmtAmount(n: number): string {
  return n.toLocaleString('en-US');
}

/** Why `fee` cannot be staked (the bounds only; the balance is the caller's), or null when it can. */
export function feeProblem(fee: number, b: FeeBounds): string | null {
  if (!Number.isSafeInteger(fee) || fee <= 0) return 'Enter a whole number of $CHAIN.';
  if (fee < b.min) return `The smallest wager is ${fmtAmount(b.min)} $CHAIN.`;
  if (fee > b.max) return `The largest wager is ${fmtAmount(b.max)} $CHAIN.`;
  return null;
}

/** What the amount field holds, as a number: digits only, nothing (or junk) = 0. */
export function parseFeeText(text: string): number {
  const digits = text.replace(/\D/g, '');
  if (digits === '') return 0;
  // More digits than any wager can have: clamp, so a pasted wall of digits stays a number.
  return Number(digits.slice(0, 12));
}

/** Only a sane answer replaces the defaults (a bad or half-deployed server leaves them standing). */
export function saneBounds(r: { fees?: unknown; min?: unknown; max?: unknown } | null | undefined): FeeBounds | null {
  if (!r || !Array.isArray(r.fees)) return null;
  const { min, max } = r;
  if (typeof min !== 'number' || typeof max !== 'number') return null;
  if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max)) return null;
  if (min < 1 || min > max) return null;
  const fees = (r.fees as unknown[]).filter(
    (f): f is number => typeof f === 'number' && Number.isSafeInteger(f) && f >= min && f <= max,
  );
  return { fees, min, max };
}

/** Open challenges (not yours: the server leaves those out) at amounts that are not a quick pick, legacy 5s included. */
export function customCount(
  counts: Readonly<Record<string, { readonly open: number } | undefined>>,
  quick: readonly number[],
): number {
  let n = 0;
  for (const [k, v] of Object.entries(counts)) if (!quick.includes(Number(k))) n += v?.open ?? 0;
  return n;
}

/**
 * The player's OWN open challenges at amounts that are not a quick pick (Create's CUSTOM tile: "N yours").
 * `byFee` is the counts endpoint's `openByFee`; the same rule as `customCount`, over the viewer's own rows, so a
 * custom-fee challenge is counted wherever a preset one would be.
 */
export function ownCustomCount(byFee: Readonly<Record<string, number | undefined>>, quick: readonly number[]): number {
  let n = 0;
  for (const [k, v] of Object.entries(byFee)) if (!quick.includes(Number(k))) n += v ?? 0;
  return n;
}

/** The Open now list under the Custom filter: only non-quick amounts, smallest first, oldest first among equals. */
export function customOnly<T extends { entryFee: number; createdAt: string }>(
  list: readonly T[],
  quick: readonly number[],
): T[] {
  return list
    .filter((c) => !quick.includes(c.entryFee))
    .sort((a, b) => a.entryFee - b.entryFee || Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

/**
 * The Open now rows: under the Custom filter only non-quick amounts (smallest first); else the stakes nearest `fee`
 * first, oldest among equals, capped at `rows`. Blockari's `openRows`.
 */
export function openRows<T extends { entryFee: number; createdAt: string }>(
  list: readonly T[],
  quick: readonly number[],
  fee: number,
  customFilter: boolean,
  rows: number,
): T[] {
  if (customFilter) return customOnly(list, quick).slice(0, rows);
  return list
    .slice()
    .sort((a, b) => Math.abs(a.entryFee - fee) - Math.abs(b.entryFee - fee) || Date.parse(a.createdAt) - Date.parse(b.createdAt))
    .slice(0, rows);
}
